import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Close as CloseIcon } from '@mui/icons-material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useToast } from '../../hooks/useToast';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonateCancelButtonSx,
  harmonateDataGridSx,
  harmonateDrawerHeaderFlushSx,
  harmonateDrawerBodySx,
  harmonateDrawerAccordionGroupSx,
  harmonateDrawerAccordionSx,
  harmonateDrawerAccordionSummarySx,
  harmonateDrawerAccordionTitleSx,
  harmonateDrawerAccordionDetailsSx,
  harmonateDrawerAddIconSx,
  harmonateDrawerEditIconSx,
  harmonateDrawerDeleteIconSx,
  harmonateDialogCloseIconSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogFormContentSx,
  harmonateDialogContentSx,
  harmonateFormFieldsSx,
  harmonateTextFieldSx,
  harmonateInputLabelProps,
  harmonateSelectMenuProps,
  harmonateGridActionIconSx,
  harmonateProceedButtonSx,
  harmonateAbortButtonSx,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';

const BankDrawer = ({ open, onClose }) => {
  const { canAdd, canEdit, canDelete } = useFunctionPermissions(FC.CASH_RECONCILIATION);
  const { showSuccess, showError, showInfo } = useToast();

  // Accordion state — default expanded like Tenant Details
  const [expanded, setExpanded] = useState('banks');

  // State for banks data
  const [banks, setBanks] = useState([]);
  const [banksLoading, setBanksLoading] = useState(false);
  const [banksError, setBanksError] = useState(null);
  const [selectedBankRow, setSelectedBankRow] = useState(null);
  const [rowMenuAnchorEl, setRowMenuAnchorEl] = useState(null);
  const [rowMenuBank, setRowMenuBank] = useState(null);
  const [statusFilter, setStatusFilter] = useState('ALL');

  // State for Add Bank Dialog
  const [addBankDialogOpen, setAddBankDialogOpen] = useState(false);
  const [newBank, setNewBank] = useState({
    tenantId: '',
    bankName: '',
    bankShortName: '',
    bankCode: '',
    swiftBic: '',
    country: '',
    website: '',
    status: 'ACTIVE'
  });
  const [addBankLoading, setAddBankLoading] = useState(false);
  const [addBankError, setAddBankError] = useState(null);
  const [countryError, setCountryError] = useState('');

  // State for Edit Bank Dialog
  const [editBankDialogOpen, setEditBankDialogOpen] = useState(false);
  const [editingBank, setEditingBank] = useState(null);
  const [editBankLoading, setEditBankLoading] = useState(false);
  const [editBankError, setEditBankError] = useState(null);

  // State for Delete Bank Dialog
  const [deleteBankDialogOpen, setDeleteBankDialogOpen] = useState(false);
  const [deletingBank, setDeletingBank] = useState(null);
  const [deleteBankLoading, setDeleteBankLoading] = useState(false);
  const [deleteBankError, setDeleteBankError] = useState(null);

  // Reset state when drawer closes
  useEffect(() => {
    if (!open) {
      setBanks([]);
      setBanksError(null);
      setSelectedBankRow(null);
      setRowMenuAnchorEl(null);
      setRowMenuBank(null);
      setStatusFilter('ALL');
      setAddBankDialogOpen(false);
      setEditBankDialogOpen(false);
      setDeleteBankDialogOpen(false);
      setNewBank({
        tenantId: '',
        bankName: '',
        bankShortName: '',
        bankCode: '',
        swiftBic: '',
        country: '',
        website: '',
        status: 'ACTIVE'
      });
      setEditingBank(null);
      setDeletingBank(null);
      setAddBankError(null);
      setEditBankError(null);
      setDeleteBankError(null);
      setCountryError('');
      setExpanded('banks');
    }
  }, [open]);

  // Fetch banks data
  const fetchBanks = async () => {
    setBanksLoading(true);
    setBanksError(null);

    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');

      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';
      let url;

      if (statusFilter === 'ALL') {
        url = `${baseUrl}conductor/cash-recon/banks/by-tenant?tenant_id=${tenantId}`;
      } else {
        url = `${baseUrl}conductor/cash-recon/banks/by-tenant-and-status?tenant_id=${tenantId}&status=${statusFilter}`;
      }

      const resp = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!resp.ok) {
        throw new Error(`Failed to fetch banks: ${resp.status} ${resp.statusText}`);
      }

      const data = await resp.json();

      if (Array.isArray(data)) {
        const processedData = data.map((item, index) => ({
          id: item.bankId || index + 1,
          bankName: item.bankName || '',
          bankCode: item.bankCode || '',
          bankShortName: item.bankShortName || '',
          swiftBic: item.swiftBic || '',
          country: item.country || '',
          website: item.website || '',
          status: item.status || 'ACTIVE',
          isActive: item.status === 'ACTIVE',
          rawData: item,
        }));
        setBanks(processedData);
      } else {
        throw new Error('Invalid response format from banks API');
      }
    } catch (err) {
      console.error('Error fetching banks:', err);
      setBanksError(err.message || 'Failed to load banks');
      setBanks([]);
    } finally {
      setBanksLoading(false);
    }
  };

  // Fetch banks when drawer opens or status filter changes
  useEffect(() => {
    if (open) {
      fetchBanks();
    }
  }, [open, statusFilter]);

  // Handle Add Bank
  const handleAddBank = async () => {
    if (!newBank.bankName.trim()) {
      setAddBankError('Bank Name is required');
      return;
    }

    if (newBank.country && newBank.country.length > 2) {
      setCountryError('Country code must be maximum 2 characters');
      return;
    }

    setAddBankLoading(true);
    setAddBankError(null);
    setCountryError('');

    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');

      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';
      const url = `${baseUrl}conductor/cash-recon/banks`;

      const payload = {
        tenantId: tenantId,
        bankName: newBank.bankName.trim(),
        bankShortName: newBank.bankShortName.trim(),
        bankCode: newBank.bankCode.trim(),
        swiftBic: newBank.swiftBic.trim(),
        country: newBank.country.trim(),
        website: newBank.website.trim(),
        status: newBank.status
      };

      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!resp.ok) {
        throw new Error(`Failed to add bank: ${resp.status} ${resp.statusText}`);
      }

      const data = await resp.json();

      console.log('Add bank API response:', data); // Debug log

      // Handle the response structure - be more flexible with validation
      if (data && (data.bankId || data.id)) {
        showSuccess('Bank added successfully');
        await fetchBanks();
        setAddBankDialogOpen(false);
        setNewBank({
          tenantId: '',
          bankName: '',
          bankShortName: '',
          bankCode: '',
          swiftBic: '',
          country: '',
          website: '',
          status: 'ACTIVE'
        });
      } else {
        console.error('Invalid response format from bank API:', data);
        throw new Error('Invalid response format from bank API');
      }
    } catch (err) {
      console.error('Error adding bank:', err);
      setAddBankError(err.message || 'Failed to add bank');
    } finally {
      setAddBankLoading(false);
    }
  };

  // Handle Edit Bank
  const handleEditBank = async () => {
    if (!editingBank || !editingBank.bankName.trim()) {
      setEditBankError('Bank Name is required');
      return;
    }

    setEditBankLoading(true);
    setEditBankError(null);

    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');

      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';
      const url = `${baseUrl}conductor/cash-recon/banks/${editingBank.id}`;

      const payload = {
        tenantId: tenantId,
        bankName: editingBank.bankName.trim(),
        bankShortName: editingBank.bankShortName.trim(),
        bankCode: editingBank.bankCode.trim(),
        swiftBic: editingBank.swiftBic.trim(),
        country: editingBank.country.trim(),
        website: editingBank.website.trim(),
        status: editingBank.status
      };

      const resp = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!resp.ok) {
        throw new Error(`Failed to update bank: ${resp.status} ${resp.statusText}`);
      }

      const data = await resp.json();

      console.log('Edit bank API response:', data); // Debug log

      // Handle the response structure - be more flexible with validation
      if (data && (data.bankId || data.id)) {
        showSuccess('Bank updated successfully');
        await fetchBanks();
        setEditBankDialogOpen(false);
        setEditingBank(null);
      } else {
        console.error('Invalid response format from bank API:', data);
        throw new Error('Invalid response format from bank API');
      }
    } catch (err) {
      console.error('Error updating bank:', err);
      setEditBankError(err.message || 'Failed to update bank');
    } finally {
      setEditBankLoading(false);
    }
  };

  // Handle Delete Bank
  const handleDeleteBank = async () => {
    if (!deletingBank) return;

    setDeleteBankLoading(true);
    setDeleteBankError(null);

    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');

      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';
      const url = `${baseUrl}conductor/cash-recon/banks/hard/${deletingBank.id}`;

      const resp = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!resp.ok) {
        throw new Error(`Failed to delete bank: ${resp.status} ${resp.statusText}`);
      }

      // Handle 204 No Content response (successful deletion)
      if (resp.status === 204) {
        showSuccess('Bank deleted successfully');
        await fetchBanks();
        setDeleteBankDialogOpen(false);
        setDeletingBank(null);
        return;
      }

      const data = await resp.json();

      console.log('Delete bank API response:', data); // Debug log

      // Handle the response structure - be more flexible with validation
      // For hard delete, API might return different response formats
      if (resp.status === 200 || data?.success || data?.message || data?.bankId || data?.id) {
        showSuccess('Bank deleted successfully');
        await fetchBanks();
        setDeleteBankDialogOpen(false);
        setDeletingBank(null);
      } else {
        console.error('Invalid response format from bank API:', data);
        throw new Error('Failed to delete bank');
      }
    } catch (err) {
      console.error('Error deleting bank:', err);
      setDeleteBankError(err.message || 'Failed to delete bank');
    } finally {
      setDeleteBankLoading(false);
    }
  };

  // Open Add Bank Dialog
  const handleOpenAddBankDialog = (e) => {
    e.stopPropagation();
    setAddBankDialogOpen(true);
    setAddBankError(null);
    setCountryError('');
  };

  // Open Edit Bank Dialog
  const handleOpenEditBankDialog = (e, bank = selectedBankRow) => {
    e?.stopPropagation();
    if (!bank) {
      showError('Please select a bank to edit');
      return;
    }
    setEditingBank({ ...bank });
    setEditBankDialogOpen(true);
    setEditBankError(null);
  };

  // Open Delete Bank Dialog
  const handleOpenDeleteBankDialog = (e, bank = selectedBankRow) => {
    e?.stopPropagation();
    if (!bank) {
      showError('Please select a bank to delete');
      return;
    }
    setDeletingBank(bank);
    setDeleteBankDialogOpen(true);
    setDeleteBankError(null);
  };

  // Columns for Banks Grid
  const bankColumns = [
    {
      field: 'bankName',
      headerName: 'Bank Name',
      flex: 2,
      minWidth: 150,
    },
    {
      field: 'bankShortName',
      headerName: 'Short Name',
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'country',
      headerName: 'Country',
      flex: 1,
      minWidth: 100,
    },
    {
      field: 'isActive',
      headerName: 'Status',
      flex: 0.8,
      minWidth: 80,
      valueFormatter: (params) => params.value ? 'ACTIVE' : 'INACTIVE',
    },
    {
      field: 'actions',
      headerName: '',
      width: 56,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton
          size="small"
          aria-label={`Actions for ${params.row.bankName}`}
          onClick={(event) => {
            event.stopPropagation();
            setRowMenuAnchorEl(event.currentTarget);
            setRowMenuBank(params.row);
          }}
          sx={harmonateGridActionIconSx}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 700,
          maxWidth: '100%',
          bgcolor: ht.drawerBg,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box sx={harmonateDrawerHeaderFlushSx}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Bank Management
        </Typography>
        <IconButton onClick={onClose} size="small" sx={harmonateDialogCloseIconSx}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={harmonateDrawerBodySx}>
        <Box sx={harmonateDrawerAccordionGroupSx}>
          {/* Banks Accordion — Tenant Details plain accordion */}
          <Accordion
            expanded={expanded === 'banks'}
            onChange={() => setExpanded(expanded === 'banks' ? false : 'banks')}
            disableGutters
            elevation={0}
            sx={harmonateDrawerAccordionSx}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: ht.text }} />} sx={harmonateDrawerAccordionSummarySx}>
              <Typography sx={harmonateDrawerAccordionTitleSx}>
                Banks
              </Typography>
              <IconButton
                onClick={handleOpenEditBankDialog}
                size="small"
                disabled={!selectedBankRow || !canEdit}
                aria-label="Edit bank"
                sx={harmonateDrawerEditIconSx}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                onClick={handleOpenDeleteBankDialog}
                size="small"
                disabled={!selectedBankRow || !canDelete}
                aria-label="Delete bank"
                sx={harmonateDrawerDeleteIconSx}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
              <IconButton
                onClick={handleOpenAddBankDialog}
                size="small"
                disabled={!canAdd}
                aria-label="Add bank"
                sx={harmonateDrawerAddIconSx}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                ...harmonateDrawerAccordionDetailsSx,
                height: 400,
                overflow: 'hidden',
              }}
            >
              <Box sx={{ ...harmonateFormFieldsSx, p: 1.5, flexShrink: 0 }}>
                <FormControl fullWidth size="small">
                  <InputLabel {...harmonateInputLabelProps}>Status Filter</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status Filter"
                    onChange={(e) => setStatusFilter(e.target.value)}
                    disabled={banksLoading}
                    MenuProps={harmonateSelectMenuProps}
                    sx={harmonateTextFieldSx}
                  >
                    <MenuItem value="ALL">All</MenuItem>
                    <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                    <MenuItem value="INACTIVE">INACTIVE</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {banksLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, minHeight: 200 }}>
                  <CircularProgress />
                </Box>
              ) : banksError ? (
                <Alert severity="error">{banksError}</Alert>
              ) : (
                <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', height: 320 }}>
                  <DataGridPro
                    rows={banks}
                    columns={bankColumns}
                    columnResizeMode="onEnd"
                    resizable
                    sx={{ ...harmonateDataGridSx, border: 'none', flex: 1 }}
                    getRowId={(row) => row.id}
                    rowHeight={40}
                    onRowSelectionModelChange={(newSelection) => {
                      if (newSelection.length > 0) {
                        const selectedRow = banks.find(row => row.id === newSelection[0]);
                        setSelectedBankRow(selectedRow);
                      } else {
                        setSelectedBankRow(null);
                      }
                    }}
                    slots={{
                      noRowsOverlay: () => (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 100 }}>
                          <Typography>No banks found.</Typography>
                        </Box>
                      )
                    }}
                    pagination
                    pageSize={100}
                  />
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        </Box>
      </Box>
      <Menu
        anchorEl={rowMenuAnchorEl}
        open={Boolean(rowMenuAnchorEl)}
        onClose={() => {
          setRowMenuAnchorEl(null);
          setRowMenuBank(null);
        }}
        PaperProps={{ sx: { borderRadius: ht.fieldRadius } }}
      >
        <MenuItem onClick={() => {
          handleOpenEditBankDialog(null, rowMenuBank);
          setRowMenuAnchorEl(null);
          setRowMenuBank(null);
        }}>
          Edit
        </MenuItem>
        <MenuItem onClick={() => {
          handleOpenDeleteBankDialog(null, rowMenuBank);
          setRowMenuAnchorEl(null);
          setRowMenuBank(null);
        }}>
          Delete
        </MenuItem>
      </Menu>

      {/* Add Bank Dialog */}
      <Dialog
        open={addBankDialogOpen}
        onClose={() => !addBankLoading && setAddBankDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>Add New Bank</DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
                label="Bank Name"
                fullWidth
                required
                size='small'
                value={newBank.bankName}
                onChange={(e) => setNewBank({ ...newBank, bankName: e.target.value })}
                disabled={addBankLoading}
                InputLabelProps={harmonateInputLabelProps}
                sx={harmonateTextFieldSx}
              />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Bank Short Name"
                fullWidth
                size='small'
                value={newBank.bankShortName}
                onChange={(e) => setNewBank({ ...newBank, bankShortName: e.target.value })}
                disabled={addBankLoading}
                InputLabelProps={harmonateInputLabelProps}
                sx={{ ...harmonateTextFieldSx, flex: 1 }}
              />
              <TextField
                label="Bank Code"
                fullWidth
                size='small'
                value={newBank.bankCode}
                onChange={(e) => setNewBank({ ...newBank, bankCode: e.target.value })}
                disabled={addBankLoading}
                InputLabelProps={harmonateInputLabelProps}
                sx={{ ...harmonateTextFieldSx, flex: 1 }}
              />
            </Box>
            <TextField
              label="SWIFT BIC"
              fullWidth
              size='small'
              value={newBank.swiftBic}
              onChange={(e) => setNewBank({ ...newBank, swiftBic: e.target.value })}
              disabled={addBankLoading}
              InputLabelProps={harmonateInputLabelProps}
              sx={harmonateTextFieldSx}
            />
            <TextField
              label="Country"
              fullWidth
              size='small'
              value={newBank.country}
              onChange={(e) => {
                setNewBank({ ...newBank, country: e.target.value });
                setCountryError('');
              }}
              disabled={addBankLoading}
              error={!!countryError}
              helperText={countryError}
              InputLabelProps={harmonateInputLabelProps}
              sx={harmonateTextFieldSx}
            />
            <TextField
              label="Website"
              fullWidth
              size='small'
              value={newBank.website}
              onChange={(e) => setNewBank({ ...newBank, website: e.target.value })}
              disabled={addBankLoading}
              InputLabelProps={harmonateInputLabelProps}
              sx={harmonateTextFieldSx}
            />
            <FormControl fullWidth>
              <InputLabel {...harmonateInputLabelProps}>Status</InputLabel>
              <Select
                value={newBank.status || 'ACTIVE'}
                label="Status"
                size='small'
                onChange={(e) => setNewBank({ ...newBank, status: e.target.value })}
                disabled={addBankLoading}
                MenuProps={harmonateSelectMenuProps}
                sx={harmonateTextFieldSx}
              >
                <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                <MenuItem value="INACTIVE">INACTIVE</MenuItem>
              </Select>
            </FormControl>
            {addBankError && (
              <Alert severity="error">
                {addBankError}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => !addBankLoading && setAddBankDialogOpen(false)}
            disabled={addBankLoading}
            variant="outlined"
            sx={harmonateCancelButtonSx}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddBank}
            variant="contained"
            disabled={addBankLoading}
            sx={harmonateProceedButtonSx}
          >
            {addBankLoading ? 'Adding...' : 'Add Bank'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Bank Dialog */}
      <Dialog
        open={editBankDialogOpen}
        onClose={() => !editBankLoading && setEditBankDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>Edit Bank</DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
          {editingBank && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Bank Name"
                fullWidth
                required
                size='small'
                value={editingBank.bankName}
                onChange={(e) => setEditingBank({ ...editingBank, bankName: e.target.value })}
                disabled={editBankLoading}
                InputLabelProps={harmonateInputLabelProps}
                sx={harmonateTextFieldSx}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Bank Short Name"
                  fullWidth
                  size='small'
                  value={editingBank.bankShortName}
                  onChange={(e) => setEditingBank({ ...editingBank, bankShortName: e.target.value })}
                  disabled={editBankLoading}
                  InputLabelProps={harmonateInputLabelProps}
                  sx={{ ...harmonateTextFieldSx, flex: 1 }}
                />
                <TextField
                  label="Bank Code"
                  fullWidth
                  size='small'
                  value={editingBank.bankCode}
                  onChange={(e) => setEditingBank({ ...editingBank, bankCode: e.target.value })}
                  disabled={editBankLoading}
                  InputLabelProps={harmonateInputLabelProps}
                  sx={{ ...harmonateTextFieldSx, flex: 1 }}
                />
              </Box>
              <TextField
                label="SWIFT BIC"
                fullWidth
                size='small'
                value={editingBank.swiftBic}
                onChange={(e) => setEditingBank({ ...editingBank, swiftBic: e.target.value })}
                disabled={editBankLoading}
                InputLabelProps={harmonateInputLabelProps}
                sx={harmonateTextFieldSx}
              />
              <TextField
                label="Country"
                fullWidth
                size='small'
                value={editingBank.country}
                onChange={(e) => setEditingBank({ ...editingBank, country: e.target.value })}
                disabled={editBankLoading}
                InputLabelProps={harmonateInputLabelProps}
                sx={harmonateTextFieldSx}
              />
              <TextField
                label="Website"
                fullWidth
                size='small'
                value={editingBank.website}
                onChange={(e) => setEditingBank({ ...editingBank, website: e.target.value })}
                disabled={editBankLoading}
                InputLabelProps={harmonateInputLabelProps}
                sx={harmonateTextFieldSx}
              />
              <FormControl fullWidth>
                <InputLabel {...harmonateInputLabelProps}>Status</InputLabel>
                <Select
                  value={editingBank.status || 'ACTIVE'}
                  label="Status"
                  size='small'
                  onChange={(e) => setEditingBank({ ...editingBank, status: e.target.value })}
                  disabled={editBankLoading}
                  MenuProps={harmonateSelectMenuProps}
                  sx={harmonateTextFieldSx}
                >
                  <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                  <MenuItem value="INACTIVE">INACTIVE</MenuItem>
                </Select>
              </FormControl>
              {editBankError && (
                <Alert severity="error">
                  {editBankError}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => !editBankLoading && setEditBankDialogOpen(false)}
            disabled={editBankLoading}
            variant="outlined"
            sx={harmonateCancelButtonSx}
          >
            Cancel
          </Button>
          <Button
            onClick={handleEditBank}
            variant="contained"
            disabled={editBankLoading}
            sx={harmonateProceedButtonSx}
          >
            {editBankLoading ? 'Updating...' : 'Update Bank'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Bank Dialog */}
      <Dialog
        open={deleteBankDialogOpen}
        onClose={() => setDeleteBankDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>Confirm Delete</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography>
            Are you sure you want to delete the bank "{deletingBank?.bankName}"?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setDeleteBankDialogOpen(false)}
            disabled={deleteBankLoading}
            variant="outlined"
            sx={harmonateCancelButtonSx}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteBank}
            variant="contained"
            disabled={deleteBankLoading}
            sx={harmonateAbortButtonSx}
          >
            {deleteBankLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
};

export default BankDrawer;